import React, { useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from "lucide-react";
import { useGalleryData } from "@/contexts/HomeDataContext";
import { GalleryItem } from "@/types/homePage";
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { useNavigation } from "@/hooks/useNavigation";

export const GallerySection: React.FC = () => {
  const data = useGalleryData();
  const t = useTranslations('Gallery');
  const tMega = useTranslations('megaMenu');
  const navigationItems = useNavigation();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Get Industries mega menu links from useNavigation hook
  const getIndustriesLinks = () => {
    // Find the industries navigation item
    const industriesItem = navigationItems.find(item => item.id === 'industries');
    
    if (industriesItem && industriesItem.children) {
      return industriesItem.children.map(child => ({
        label: child.label,
        href: child.href
      }));
    }
    
    // Fallback to message files if useNavigation doesn't have the data
    const links = [];
    const sectionCount = tMega.raw('industries.sectionsCount') as number || 1;
    
    for (let i = 0; i < sectionCount; i++) {
      const sectionKey = `section${i + 1}`;
      const itemsCount = tMega.raw(`industries.${sectionKey}.itemsCount`) as number || 0;
      
      for (let j = 0; j < itemsCount; j++) {
        const itemKey = `${sectionKey}.item${j + 1}`;
        const label = tMega(`industries.${itemKey}.label`);
        const href = tMega(`industries.${itemKey}.href`);
        
        if (label && href) {
          links.push({ label, href });
        }
      }
    }
    
    return links;
  };

  const industriesLinks = getIndustriesLinks();

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % data.items.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + data.items.length) % data.items.length);
  };

  return (
    <section className="bg-white">
      <div className="relative py-16 md:py-20 lg:py-24 bg-[#F9F9F9] lg:w-[85%] -mt-40 md:-mt-48 lg:-mt-40 overflow-visible">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">

          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12 md:mb-16 relative z-20 bg-[#F9F9F9] pt-4 md:pt-6 lg:pt-8">

            {/* Title Section */}
            <div className="mb-8 md:mb-0 text-center md:text-left rtl:md:text-right">
              <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
                <div className="bg-brand-gradient-text bg-clip-text text-transparent font-h1-up font-[number:var(--h1-up-font-weight)] text-[length:var(--h1-up-font-size)] tracking-[var(--h1-up-letter-spacing)] leading-[var(--h1-up-line-height)] [font-style:var(--h1-up-font-style)] mb-3 md:mb-4">
                  {t('tagline')}
                </div>

                <h2 className="font-heading-h1 font-[number:var(--heading-h1-font-weight)] text-x-900 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[length:var(--heading-h1-font-size)] tracking-[var(--heading-h1-letter-spacing)] leading-tight md:leading-tight lg:leading-[var(--heading-h1-line-height)] [font-style:var(--heading-h1-font-style)] max-w-2xl">
                  {t('heading')}
                </h2>
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex gap-3 translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms] justify-center md:justify-end rtl:md:justify-start">
              <button
                onClick={prevSlide}
                className="w-12 h-12 rounded-full border-2 border-brand-secondary bg-white hover:bg-brand-gradient transition-all duration-300 flex items-center justify-center group hover:scale-105"
              >
                <ChevronLeftIcon className="w-5 h-5 text-brand-secondary group-hover:text-white transition-colors duration-300 rtl:rotate-180" />
              </button>

              <button
                onClick={nextSlide}
                className="w-12 h-12 rounded-full bg-brand-gradient hover:opacity-90 transition-all duration-300 flex items-center justify-center group hover:scale-105"
              >
                <ChevronRightIcon className="w-5 h-5 text-white transition-colors duration-300 rtl:rotate-180" />
              </button>
            </div>
          </div>
      </div>

      {/* Gallery Slider - Outside container */}
      <div className="relative overflow-hidden py-4 z-20">
        <style jsx>{`
          .mobile-slider {
            transform: translateX(-${currentIndex * 100}%);
          }
          @media (min-width: 768px) {
            .mobile-slider {
              transform: translateX(-${currentIndex * 25}%);
            }
          }
        `}</style>
        <div
          className="flex transition-transform duration-500 ease-in-out gap-6 px-4 md:px-6 lg:px-8 mobile-slider"
        >
              {data.items.map((item: GalleryItem, index: number) => {
                // Map each card to an industry link (cycle through if more cards than links)
                const industryLink = industriesLinks[index % industriesLinks.length];
                const linkHref = industryLink?.href || '#';
                
                return (
                  <div
                    key={item.id}
                    className="w-full md:w-1/2 lg:w-1/4 flex-shrink-0"
                    style={{
                      animationDelay: `${600 + index * 200}ms`
                    }}
                  >
                    <div className="block">
                      <div className="relative group bg-white rounded-lg shadow-lg overflow-visible hover:shadow-xl transition-all duration-300">

                        {/* Gallery Image */}
                        <div className="w-full h-[512px] overflow-hidden rounded-t-lg mx-auto">
                          <img
                            src={item.image}
                            alt={item.alt}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />

                          {/* Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>

                        {/* Content - Positioned outside image boundaries */}
                        <div className="absolute -bottom-2 left-2 right-2 p-4 md:left-0 md:w-[85%] md:p-6 bg-white shadow-lg" style={{ borderRadius: '0 8px 8px 8px' }}>
                          <div className="flex items-center justify-between w-full">
                            <h3 className="font-heading-h3 font-[number:var(--heading-h3-font-weight)] text-x-900 text-lg md:text-xl lg:text-2xl tracking-[var(--heading-h3-letter-spacing)] leading-[var(--heading-h3-line-height)] [font-style:var(--heading-h3-font-style)] flex-1">
                              {industryLink?.label || item.title}
                            </h3>

                            {/* Plus Button - Hidden until destination pages are created */}
                            {/* <div className="w-8 h-8 md:w-10 md:h-10 rounded-md bg-white border-2 border-brand-secondary transition-all duration-300 flex items-center justify-center hover:bg-brand-gradient hover:scale-110 flex-shrink-0 ml-4 group/plus">
                              <PlusIcon className="w-4 h-4 md:w-5 md:h-5 text-brand-secondary transition-colors duration-300 group-hover/plus:text-white" />
                            </div> */}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      
    </section>
  );
};